import React, { useState, useEffect } from 'react'
import { useAuth } from '../domains/auth/AuthContext'
import { reportsApi, locationsApi } from '../services/api'
import { offlineStorage } from '../services/offlineStorage'
import { checkInStateManager } from '../services/checkInStateManager'
import toast from 'react-hot-toast'
import {
  ClockIcon,
  MapPinIcon,
  DocumentTextIcon,
  TagIcon,
  CameraIcon,
  PaperAirplaneIcon,
  UserIcon,
  BuildingOfficeIcon,
  TrashIcon
} from '@heroicons/react/24/outline'

interface HourlyReportForm {
  organizationId?: string
  shiftStart: string
  shiftEnd: string
  summary: string
  activityTags: string[]
  latitude?: number
  longitude?: number
  locationId?: string
  // New fields for detailed hourly report
  clientName?: string
  clientAddress?: string
  officerOnDuty?: string
  officerRelieved?: string
  officerWorkingWith?: string
  specialAttention?: string
  outOfOrderCondition?: string
  irregularitiesFound?: Array<{ item: string; found: boolean }>
  hourlyActivityLog?: Array<{ timeRange: string; activity: string }>
}

interface IrregularityItem {
  item: string
  found: boolean
}

interface ActivityLogEntry {
  timeRange: string
  activity: string
}

export default function HourlyReport() {
  const { user } = useAuth()
  // Helper function to get current datetime in datetime-local format
  const getCurrentDateTime = () => {
    const now = new Date()
    const offset = now.getTimezoneOffset() * 60000
    return new Date(now.getTime() - offset).toISOString().slice(0, 16)
  }

  const [form, setForm] = useState<HourlyReportForm>({
    organizationId: undefined,
    shiftStart: getCurrentDateTime(),
    shiftEnd: getCurrentDateTime(),
    summary: '',
    activityTags: [],
    latitude: undefined,
    longitude: undefined,
    locationId: undefined,
    clientName: '',
    clientAddress: '',
    officerOnDuty: '',
    officerRelieved: '',
    officerWorkingWith: '',
    specialAttention: '',
    outOfOrderCondition: '',
    irregularitiesFound: [],
    hourlyActivityLog: []
  })

  const [serverDraftId, setServerDraftId] = useState<string | null>(null)
  const [localDraftId, setLocalDraftId] = useState<string | null>(null)
  const [isDraftSaving, setIsDraftSaving] = useState(false)
  const [savedFields, setSavedFields] = useState<Set<string>>(new Set())
  const [savedFieldsDebug, setSavedFieldsDebug] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number } | null>(null)
  const [currentCheckInLocation, setCurrentCheckInLocation] = useState<any | null>(null)
  const [irregularities, setIrregularities] = useState<string[]>([])
  const [newActivityLog, setNewActivityLog] = useState<ActivityLogEntry>({
    timeRange: '',
    activity: ''
  })

  const activityOptions = [
    'Patrol', 'Security Check', 'Access Control', 'Incident Response',
    'Customer Service', 'Maintenance', 'Training', 'Administrative',
    'Emergency Response', 'Documentation', 'Equipment Check'
  ]

  useEffect(() => {
    // Ensure organizationId is populated from the authenticated context
    if (user?.currentOrganization?.id) {
      setForm(prev => ({ ...prev, organizationId: user.currentOrganization!.id }))
    }

    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
          setForm(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }))
        },
        (error) => {
          console.error('Error getting location:', error)
        }
      )
    }

    // Load current check-in location
    loadCurrentCheckInLocation()

    // Load organization irregularities first, then drafts
    const loadData = async () => {
      await loadIrregularities()
      await loadExistingDraft()
    }
    loadData()
  }, [])

  // Load current check-in location for auto-assignment
  const loadCurrentCheckInLocation = async () => {
    try {
      const checkInState = await checkInStateManager.getCurrentCheckInState()
      if (checkInState) {
        const checkInLocation = {
          id: checkInState.locationId,
          name: checkInState.locationName,
          address: checkInState.locationAddress,
          organizationId: '',
          centerLat: null,
          centerLng: null,
          radiusMeters: null
        }
        console.log('📍 Auto-assigning check-in location for hourly report:', checkInLocation.name)
        setCurrentCheckInLocation(checkInLocation)

        // Auto-populate client name, address, and locationId from check-in location
        setForm(prev => ({
          ...prev,
          locationId: checkInLocation.id,
          clientName: checkInLocation.name,
          clientAddress: checkInLocation.address
        }))
      }
    } catch (error) {
      console.error('Error loading current check-in location:', error)
    }
  }

  // Monitor savedFields changes
  useEffect(() => {
    console.log('🔍 savedFields changed:', Array.from(savedFields))
  }, [savedFields])

  const loadIrregularities = async () => {
    try {
      const response = await reportsApi.getOrganizationIrregularities()
      console.log('🔍 Irregularities response:', response)

      // Extract descriptions from the response
      const irregularitiesData = response.data || []
      const irregularityStrings = irregularitiesData.map((item: any) =>
        typeof item === 'string' ? item : item.description || item.type || 'Unknown'
      )

      console.log('🔍 Processed irregularities:', irregularityStrings)
      setIrregularities(irregularityStrings)

      // Initialize irregularitiesFound with all available irregularities (found: false)
      const initialIrregularitiesFound = irregularityStrings.map((item: string) => ({
        item,
        found: false
      }))

      // Only initialize if irregularitiesFound is empty (first time loading)
      setForm(prev => ({
        ...prev,
        irregularitiesFound: prev.irregularitiesFound && prev.irregularitiesFound.length > 0
          ? prev.irregularitiesFound
          : initialIrregularitiesFound
      }))

      console.log('🔍 Initialized irregularitiesFound:', initialIrregularitiesFound)
    } catch (error) {
      console.error('Error loading irregularities:', error)
    }
  }

  // Helper function to convert ISO datetime to datetime-local format
  const convertISOToDateTimeLocal = (isoString: string): string => {
    if (!isoString) return ''
    try {
      const date = new Date(isoString)
      if (isNaN(date.getTime())) return ''

      // Convert to local timezone
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day}T${hours}:${minutes}`
    } catch (error) {
      console.error('Error converting ISO to datetime-local:', error)
      return ''
    }
  }

  const normalizeDateTimeLocal = (v?: string) => {
    if (!v) return ''
    // If already in YYYY-MM-DDTHH:mm format, respect it
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return v
    // If it comes as ISO, convert it to local
    const d = new Date(v)
    if (isNaN(d.getTime())) return ''
    const off = d.getTimezoneOffset() * 60000
    return new Date(d.getTime() - off).toISOString().slice(0, 16)
  }

  const toDatetimeLocal = (value?: string | Date | null) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  };

  // Load existing draft on component mount
  const loadExistingDraft = async () => {
    try {
      let latestDraft: any = null

      // First check for offline drafts
      const offlineDraft = offlineStorage.getLocalReportDraft('hourly')
      if (offlineDraft) {
        console.log('📱 Found offline hourly draft:', offlineDraft)
        latestDraft = {
          id: offlineDraft.localId,
          ...offlineDraft.data,
          offline: true
        }
        setLocalDraftId(offlineDraft.localId)
      }

      // Then check server drafts if online
      if (navigator.onLine) {
        try {
          const draftsResponse = await reportsApi.getDraftHourlyReports()
          const serverDrafts = draftsResponse?.data || []

          console.log('📋 Found server hourly drafts:', serverDrafts.length)

          if (serverDrafts.length > 0) {
            const serverDraft = serverDrafts[0] // Most recent draft
            setServerDraftId(serverDraft.id)

            // If we have both offline and server drafts, merge them intelligently
            if (offlineDraft) {
              console.log('📋 Found both offline and server drafts, merging...')
              // Merge offline changes with server data
              const mergedData = {
                ...serverDraft,
                ...offlineDraft.data,
                // Preserve arrays from offline draft if they have content
                activityTags: offlineDraft.data.activityTags?.length ? offlineDraft.data.activityTags : serverDraft.activityTags,
                irregularitiesFound: offlineDraft.data.irregularitiesFound?.length ? offlineDraft.data.irregularitiesFound : serverDraft.irregularitiesFound,
                hourlyActivityLog: offlineDraft.data.hourlyActivityLog?.length ? offlineDraft.data.hourlyActivityLog : serverDraft.hourlyActivityLog
              }

              // Update the server draft with merged data
              await reportsApi.updateHourlyReportDraft(serverDraft.id, mergedData)
              console.log('📋 Merged offline changes with server draft')

              // Clear the offline draft since it's now synced
              offlineStorage.clearReportDraft(offlineDraft.localId)
              setLocalDraftId(null)

              // Use merged data as the latest draft
              latestDraft = {
                id: serverDraft.id,
                ...mergedData,
                offline: false
              }
            } else {
              // Only server draft exists, use it
              latestDraft = {
                id: serverDraft.id,
                ...serverDraft,
                offline: false
              }
            }
          } else {
            // No server draft exists, create one from offline data if available
            if (offlineDraft) {
              console.log('📋 Creating server draft from offline data...')
              const response = await reportsApi.createHourlyReportViaReports({
                ...offlineDraft.data,
                isDraft: true
              })
              setServerDraftId(response.id)
              console.log('📋 Created server draft:', response.id)

              // Clear the offline draft since it's now synced
              offlineStorage.clearReportDraft(offlineDraft.localId)
              setLocalDraftId(null)
            }
          }
        } catch (error) {
          console.log('Server draft loading failed, using offline draft only:', error)
        }
      }

      if (latestDraft) {
        console.log('📋 Loading draft data:', latestDraft)
        console.log('📋 Draft field values:')
        console.log('  - summary:', latestDraft.summary)
        console.log('  - clientName:', latestDraft.clientName)
        console.log('  - officerOnDuty:', latestDraft.officerOnDuty)
        console.log('  - activityTags:', latestDraft.activityTags)
        console.log('  - irregularitiesFound:', latestDraft.irregularitiesFound)
        console.log('  - hourlyActivityLog:', latestDraft.hourlyActivityLog)

        // Set the appropriate ID based on whether it's offline or server draft
        if (latestDraft.offline) {
          setLocalDraftId(latestDraft.localId || latestDraft.id)   // <- local storage id
        } else {
          setServerDraftId(latestDraft.id)       // <- server id
        }

        setForm(prev => {
          const newFormData = {
            ...prev,
            organizationId: latestDraft.organizationId ?? prev.organizationId,
            shiftStart: normalizeDateTimeLocal(latestDraft.shiftStart) || prev.shiftStart,
            shiftEnd: normalizeDateTimeLocal(latestDraft.shiftEnd) || prev.shiftEnd,
            // Keep arrays if they exist
            activityTags: Array.isArray(latestDraft.activityTags) ? latestDraft.activityTags : prev.activityTags,
            irregularitiesFound: Array.isArray(latestDraft.irregularitiesFound) && latestDraft.irregularitiesFound.length
              ? latestDraft.irregularitiesFound
              : (prev.irregularitiesFound?.length ? prev.irregularitiesFound : irregularities.map(i => ({ item: i, found: false }))),
            hourlyActivityLog: Array.isArray(latestDraft.hourlyActivityLog) ? latestDraft.hourlyActivityLog : prev.hourlyActivityLog,
            // Rest of fields...
            summary: latestDraft.summary && latestDraft.summary !== 'Draft in progress...' ? latestDraft.summary : prev.summary,
            clientName: latestDraft.clientName ?? prev.clientName,
            clientAddress: latestDraft.clientAddress ?? prev.clientAddress,
            officerOnDuty: latestDraft.officerOnDuty ?? prev.officerOnDuty,
            officerRelieved: latestDraft.officerRelieved ?? prev.officerRelieved,
            officerWorkingWith: latestDraft.officerWorkingWith ?? prev.officerWorkingWith,
            specialAttention: latestDraft.specialAttention ?? prev.specialAttention,
            outOfOrderCondition: latestDraft.outOfOrderCondition ?? prev.outOfOrderCondition,
            latitude: latestDraft.latitude ?? prev.latitude,
            longitude: latestDraft.longitude ?? prev.longitude,
            locationId: latestDraft.locationId ?? prev.locationId
          }

          console.log('📋 Setting form with new data:', newFormData)
          return newFormData
        })

        // Show offline indicator if applicable
        if (latestDraft.offline) {
          toast.success('Loaded offline draft - will sync when connection is restored')
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error)
    }
  }

  // Auto-save draft when field loses focus
  const autoSaveDraft = async (fieldName: keyof HourlyReportForm | string, updatedData?: any) => {
    let payload: any = null
    try {
      setIsDraftSaving(true)

      // 1) Clone the current form and apply the change if provided
      const nextForm: HourlyReportForm = { ...form }
      if (updatedData !== undefined) {
        (nextForm as any)[fieldName] = updatedData
      }

      // 2) Merge with what's already saved locally (if it exists)
      let existingLocal = offlineStorage.getLocalReportDraft('hourly') // assumes unique per type
      const baseData = existingLocal?.data || {}

      // 3) Build complete payload (not partial)
      payload = {
        ...baseData,          // what already existed
        ...nextForm,          // current form state (with updatedData applied)
        isDraft: true
      }

      // Always include organizationId from the authenticated context
      if (user?.currentOrganization?.id) {
        payload.organizationId = user.currentOrganization.id
      }

      // Ensure arrays are present (prevents "disappearances" from weird merges):
      payload.activityTags = Array.isArray(payload.activityTags) ? payload.activityTags : []
      payload.irregularitiesFound = Array.isArray(payload.irregularitiesFound) ? payload.irregularitiesFound : irregularities.map((i: string) => ({ item: i, found: false }))
      payload.hourlyActivityLog = Array.isArray(payload.hourlyActivityLog) ? payload.hourlyActivityLog : []

      // Time defaults in local format (not raw UTC)
      const nowLocal = (() => {
        const d = new Date()
        const off = d.getTimezoneOffset() * 60000
        return new Date(d.getTime() - off).toISOString().slice(0, 16)
      })()
      if (!payload.shiftStart) payload.shiftStart = nowLocal
      if (!payload.shiftEnd) payload.shiftEnd = nowLocal
      if (!payload.summary?.trim()) payload.summary = 'Draft in progress...'

      const isOffline = !navigator.onLine

      if (isOffline) {
        // ---- OFFLINE: save using localDraftId if it exists, if not, create ----
        const storedId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          localId: localDraftId || undefined,    // <- IMPORTANT: points to the same record
          reportId: serverDraftId || undefined,  // cross-reference (optional) if server already exists
          data: payload,
          mediaFiles: []
        })
        // Only set localDraftId if it was creation
        if (!localDraftId) setLocalDraftId(storedId)

        toast.success(`${String(fieldName)} saved offline`)
      } else {
        // ---- ONLINE ----
        if (serverDraftId) {
          await reportsApi.updateHourlyReportDraft(serverDraftId, payload)
        } else {
          const resp = await reportsApi.createHourlyReportViaReports(payload)
          setServerDraftId(resp.id)
        }
      }

      setSavedFields(prev => new Set([...prev, String(fieldName)]))

    } catch (err: any) {
      console.error('❌ Error auto-saving draft:', err, '\nField:', fieldName, '\nPayload:', payload)
      toast.error(`Failed to save ${String(fieldName)}: ${err.response?.data?.error || err.message}`)
    } finally {
      setIsDraftSaving(false)
    }
  }

  // Clear ALL drafts for this user
  const clearDraft = async () => {
    try {
      // Clear offline draft if it exists
      if (localDraftId) {
        offlineStorage.clearReportDraft(localDraftId)
        setLocalDraftId(null)
      }

      // Clear server draft if it exists
      if (serverDraftId) {
        try {
          await reportsApi.deleteHourlyReportDraft(serverDraftId)
          setServerDraftId(null)
        } catch (error) {
          console.warn('Could not delete server draft:', error)
          // Still clear the local reference
          setServerDraftId(null)
        }
      }

      // Reset form with current datetime
      setForm({
        shiftStart: getCurrentDateTime(),
        shiftEnd: getCurrentDateTime(),
        summary: '',
        activityTags: [],
        latitude: undefined,
        longitude: undefined,
        locationId: undefined,
        clientName: '',
        clientAddress: '',
        officerOnDuty: '',
        officerRelieved: '',
        officerWorkingWith: '',
        specialAttention: '',
        outOfOrderCondition: '',
        irregularitiesFound: [],
        hourlyActivityLog: []
      })

      setSavedFields(new Set())
      setSavedFieldsDebug(new Set())

      // Reload current check-in location after clearing
      loadCurrentCheckInLocation()

      toast.success('Draft cleared successfully')
    } catch (error) {
      console.error('Error clearing draft:', error)
      toast.error('Failed to clear draft')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Check if we're offline
      const isOffline = !navigator.onLine

      if (isOffline) {
        console.log('📱 Offline mode: storing report as draft offline')

        // Store the current form data as an offline draft
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          reportId: localDraftId ?? undefined, // Keep reference to server ID if it exists
          data: {
            ...form,
            activityTags: form.activityTags,
            isDraft: true // Keep as draft until we can sync
          },
          mediaFiles: []
        })

        setLocalDraftId(localId)
        toast.success('Report saved offline as draft - will submit when connection is restored')

        // Clear form and draft state
        setSuccess(true)
        setForm({
          shiftStart: getCurrentDateTime(),
          shiftEnd: getCurrentDateTime(),
          summary: '',
          activityTags: [],
          latitude: undefined,
          longitude: undefined,
          locationId: undefined,
          clientName: '',
          clientAddress: '',
          officerOnDuty: '',
          officerRelieved: '',
          officerWorkingWith: '',
          specialAttention: '',
          outOfOrderCondition: '',
          irregularitiesFound: [],
          hourlyActivityLog: []
        })
        setServerDraftId(null)
        setLocalDraftId(null)
        setSavedFieldsDebug(new Set())

        return
      }

      // Online mode - proceed with normal submission
      if (serverDraftId) {
        // Publish existing draft - this should set isDraft to false on the backend
        console.log('📤 Publishing draft with ID:', serverDraftId)
        const publishedReport = await reportsApi.publishHourlyReport(serverDraftId)
        console.log('✅ Draft published successfully:', publishedReport)
        toast.success('Hourly report submitted successfully')

        // Clear both IDs after successful submission
        setServerDraftId(null)
        setLocalDraftId(null)
      } else if (localDraftId) {
        // We have a local draft but no server ID - create new report
        console.log('📤 Creating new report from local draft')
        const response = await reportsApi.createHourlyReportViaReports({
          ...form,
          organizationId: user?.currentOrganization?.id || form.organizationId,
          isDraft: false
        })
        console.log('✅ Report created successfully:', response)
        toast.success('Hourly report submitted successfully')

        // Clear local ID after successful submission
        setLocalDraftId(null)
      } else {
        // No draft exists - create new report
        console.log('📤 Creating new report')
        console.log('📋 Form data being sent:', { ...form, organizationId: user?.currentOrganization?.id || form.organizationId, isDraft: false })
        const response = await reportsApi.createHourlyReportViaReports({
          ...form,
          organizationId: user?.currentOrganization?.id || form.organizationId,
          isDraft: false
        })
        console.log('✅ Report created successfully:', response)
        toast.success('Hourly report submitted successfully')
      }

      // Clear form and draft state
      setSuccess(true)
      setForm({
        shiftStart: getCurrentDateTime(),
        shiftEnd: getCurrentDateTime(),
        summary: '',
        activityTags: [],
        latitude: undefined,
        longitude: undefined,
        locationId: undefined,
        clientName: '',
        clientAddress: '',
        officerOnDuty: '',
        officerRelieved: '',
        officerWorkingWith: '',
        specialAttention: '',
        outOfOrderCondition: '',
        irregularitiesFound: [],
        hourlyActivityLog: []
      })
      setServerDraftId(null)
      setLocalDraftId(null)
      setSavedFieldsDebug(new Set())
    } catch (err: any) {
      console.error('Error submitting hourly report:', err)
      toast.error(err.response?.data?.error || 'Failed to submit hourly report')
      setError(err.response?.data?.error || 'Failed to create hourly report')
    } finally {
      setLoading(false)
    }
  }

  const handleActivityTagToggle = (tag: string) => {
    setForm(prev => {
      const updatedActivityTags = prev.activityTags.includes(tag)
        ? prev.activityTags.filter(t => t !== tag)
        : [...prev.activityTags, tag]

      // Auto-save with the updated data immediately
      autoSaveDraft('activityTags', updatedActivityTags)

      return {
        ...prev,
        activityTags: updatedActivityTags
      }
    })
  }

  const handleIrregularityToggle = (item: string) => {
    setForm(prev => {
      // Ensure all available irregularities are included
      const currentIrregularitiesFound = prev.irregularitiesFound || []
      const allIrregularities = irregularities.map((irregularityItem: string) => {
        const existing = currentIrregularitiesFound.find(i => i.item === irregularityItem)
        if (irregularityItem === item) {
          // Toggle the found status for the clicked item
          return {
            item: irregularityItem,
            found: existing ? !existing.found : true
          }
        }
        // Keep existing status for other items
        return existing || { item: irregularityItem, found: false }
      })

      // Auto-save with the updated data immediately
      autoSaveDraft('irregularitiesFound', allIrregularities)

      return {
        ...prev,
        irregularitiesFound: allIrregularities
      }
    })
  }

  const addActivityLog = () => {
    if (newActivityLog.timeRange && newActivityLog.activity) {
      setForm(prev => {
        const updatedHourlyActivityLog = [...(prev.hourlyActivityLog || []), newActivityLog]

        // Auto-save with the updated data immediately
        autoSaveDraft('hourlyActivityLog', updatedHourlyActivityLog)

        return {
          ...prev,
          hourlyActivityLog: updatedHourlyActivityLog
        }
      })
      setNewActivityLog({ timeRange: '', activity: '' })
    }
  }

  const removeActivityLog = (index: number) => {
    setForm(prev => {
      const updatedHourlyActivityLog = prev.hourlyActivityLog?.filter((_, i) => i !== index) || []

      // Auto-save with the updated data immediately
      autoSaveDraft('hourlyActivityLog', updatedHourlyActivityLog)

      return {
        ...prev,
        hourlyActivityLog: updatedHourlyActivityLog
      }
    })
  }

  if (success) {
    return (
      <div className="p-4 pb-32">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PaperAirplaneIcon className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-green-800 mb-2">Report Submitted!</h2>
          <p className="text-green-700 mb-4">Your hourly report has been successfully submitted.</p>
          <button
            onClick={() => setSuccess(false)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Submit Another Report
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 pb-32 space-y-6 overflow-y-auto">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary-orange rounded-lg flex items-center justify-center">
            <ClockIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-tech-50">Hourly Report</h1>
            <p className="text-gray-600 dark:text-tech-400">Submit your detailed shift report</p>
            {/* {currentDraftId && ( // This line was removed as per the edit hint
              <div className="flex items-center space-x-2 mt-2">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-blue-600 dark:text-blue-400">Draft loaded - Auto-saving enabled</span>
                </div>
              </div>
            )} */}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Shift Times */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Shift Start Time
              </label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={form.shiftStart}
                  readOnly
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-tech-900 border border-gray-300 dark:border-tech-700 rounded-lg text-gray-900 dark:text-tech-100 cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-tech-400 mt-1">
                Auto-set to current time
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Shift End Time
              </label>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={form.shiftEnd}
                  readOnly
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-tech-900 border border-gray-300 dark:border-tech-700 rounded-lg text-gray-900 dark:text-tech-100 cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-tech-400 mt-1">
                Auto-set to current time
              </p>
            </div>
          </div>

          {/* Site/Client Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Site/Client Name
              </label>
              {currentCheckInLocation ? (
                <div className="relative">
                  <div className="w-full px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-900 dark:text-tech-100 font-medium">
                      {currentCheckInLocation.name}
                    </span>
                    <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Current Check-in Location
                    </div>
                  </div>
                  {savedFields.has('clientName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={form.clientName}
                    onChange={(e) => setForm(prev => ({ ...prev, clientName: e.target.value }))}
                    onBlur={() => autoSaveDraft('clientName')}
                    placeholder="Name of business/property being secured"
                    className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                  />
                  {savedFields.has('clientName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Site Address
              </label>
              {currentCheckInLocation ? (
                <div className="relative">
                  <div className="w-full px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-900 dark:text-tech-100 font-medium">
                      {currentCheckInLocation.address}
                    </span>
                    <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Current Check-in Location
                    </div>
                  </div>
                  {savedFields.has('clientAddress') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={form.clientAddress}
                    onChange={(e) => setForm(prev => ({ ...prev, clientAddress: e.target.value }))}
                    onBlur={() => autoSaveDraft('clientAddress')}
                    placeholder="Address of the secured location"
                    className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                  />
                  {savedFields.has('clientAddress') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                  )}
                </div>
              )}
              {!currentCheckInLocation && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  ⚠️ You are not currently checked in. Site information may need to be entered manually.
                </p>
              )}
            </div>
          </div>

          {/* Officer Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Officer on Duty
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={form.officerOnDuty}
                  onChange={(e) => setForm(prev => ({ ...prev, officerOnDuty: e.target.value }))}
                  onBlur={() => autoSaveDraft('officerOnDuty')}
                  className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                />
                {savedFields.has('officerOnDuty') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-green-500 text-sm">✓</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Officer Relieved
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={form.officerRelieved}
                  onChange={(e) => setForm(prev => ({ ...prev, officerRelieved: e.target.value }))}
                  onBlur={() => autoSaveDraft('officerRelieved')}
                  className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                />
                {savedFields.has('officerRelieved') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-green-500 text-sm">✓</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Officer you are working with?
            </label>
            <div className="relative">
              <input
                type="text"
                value={form.officerWorkingWith}
                onChange={(e) => setForm(prev => ({ ...prev, officerWorkingWith: e.target.value }))}
                onBlur={() => autoSaveDraft('officerWorkingWith')}
                placeholder="Enter officer name..."
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              />
              {savedFields.has('officerWorkingWith') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>

          {/* Special Attention and Out of Order */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Special Attention
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={form.specialAttention}
                  onChange={(e) => setForm(prev => ({ ...prev, specialAttention: e.target.value }))}
                  onBlur={() => autoSaveDraft('specialAttention')}
                  className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                />
                {savedFields.has('specialAttention') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-green-500 text-sm">✓</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
                Out of Order Condition
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={form.outOfOrderCondition}
                  onChange={(e) => setForm(prev => ({ ...prev, outOfOrderCondition: e.target.value }))}
                  onBlur={() => autoSaveDraft('outOfOrderCondition')}
                  className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                />
                {savedFields.has('outOfOrderCondition') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <span className="text-green-500 text-sm">✓</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Irregularities Found */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Irregularities Found
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {irregularities.map((item: string) => {
                const isFound = form.irregularitiesFound?.find(i => i.item === item)?.found || false
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleIrregularityToggle(item)}
                    className={`p-2 text-sm rounded-lg border transition-colors ${isFound
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-300 dark:border-red-700'
                      : 'bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-100 border-gray-300 dark:border-tech-700 hover:border-primary-orange dark:hover:border-primary-500'
                      }`}
                  >
                    {item}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Hourly Activity Log */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-3">
              Hourly Activity Log
            </label>
            <div className="space-y-4">
              {/* Existing Activity Log Entries */}
              {form.hourlyActivityLog?.map((log, index) => (
                <div key={index} className="bg-gray-50 dark:bg-tech-800/30 p-4 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-tech-300">Entry #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeActivityLog(index)}
                      className="flex items-center space-x-1 px-2 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      <span className="text-sm">Remove</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-tech-400 mb-1">
                        Time Range
                      </label>
                      <input
                        type="text"
                        value={log.timeRange}
                        onChange={(e) => {
                          const updated = [...(form.hourlyActivityLog || [])]
                          updated[index] = { ...log, timeRange: e.target.value }
                          setForm(prev => ({ ...prev, hourlyActivityLog: updated }))
                        }}
                        onBlur={() => {
                          const updated = [...(form.hourlyActivityLog || [])]
                          autoSaveDraft('hourlyActivityLog', updated)
                        }}
                        placeholder="e.g., 17:40-18:40"
                        className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-tech-400 mb-1">
                        Activity Description
                      </label>
                      <input
                        type="text"
                        value={log.activity}
                        onChange={(e) => {
                          const updated = [...(form.hourlyActivityLog || [])]
                          updated[index] = { ...log, activity: e.target.value }
                          setForm(prev => ({ ...prev, hourlyActivityLog: updated }))
                        }}
                        onBlur={() => {
                          const updated = [...(form.hourlyActivityLog || [])]
                          autoSaveDraft('hourlyActivityLog', updated)
                        }}
                        placeholder="Describe the activity"
                        className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Activity Log Entry */}
              <div className="border-2 border-dashed border-gray-300 dark:border-tech-600 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-tech-300 mb-3">Add New Activity</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-tech-400 mb-1">
                      Time Range
                    </label>
                    <input
                      type="text"
                      value={newActivityLog.timeRange}
                      onChange={(e) => setNewActivityLog(prev => ({ ...prev, timeRange: e.target.value }))}
                      placeholder="e.g., 19:00-20:00"
                      className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-tech-400 mb-1">
                      Activity Description
                    </label>
                    <input
                      type="text"
                      value={newActivityLog.activity}
                      onChange={(e) => setNewActivityLog(prev => ({ ...prev, activity: e.target.value }))}
                      placeholder="Describe what you did"
                      className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addActivityLog}
                  disabled={!newActivityLog.timeRange || !newActivityLog.activity}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ClockIcon className="w-4 h-4" />
                  <span>Add Activity Log Entry</span>
                </button>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Shift Summary
            </label>
            <div className="relative">
              <textarea
                value={form.summary}
                onChange={(e) => setForm(prev => ({ ...prev, summary: e.target.value }))}
                onBlur={(e) => autoSaveDraft('summary', e.target.value)}
                required
                rows={4}
                placeholder="Describe your activities during this shift..."
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              />
              {savedFields.has('summary') && (
                <div className="absolute right-3 top-3">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>

          {/* Activity Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Activities Performed
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {activityOptions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleActivityTagToggle(tag)}
                  className={`p-2 text-sm rounded-lg border transition-colors ${form.activityTags.includes(tag)
                    ? 'bg-primary-orange text-white border-primary-orange'
                    : 'bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-100 border-gray-300 dark:border-tech-700 hover:border-primary-orange dark:hover:border-primary-500'
                    }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Location
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-tech-800 rounded-lg">
              <MapPinIcon className="w-5 h-5 text-gray-500 dark:text-tech-400" />
              {currentLocation ? (
                <span className="text-sm text-gray-700 dark:text-tech-300">
                  {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
                </span>
              ) : (
                <span className="text-sm text-gray-500 dark:text-tech-400">Getting location...</span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Clear Report Button - Show if there's draft data or saved fields */}
          {(serverDraftId || localDraftId || savedFields.size > 0) && (
            <button
              type="button"
              onClick={clearDraft}
              className="w-full bg-red-600 dark:bg-red-700 text-white py-3 rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-600 flex items-center justify-center gap-2 mb-4 transition-colors"
            >
              <TrashIcon className="w-5 h-5" />
              Clear Report
            </button>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-orange text-white py-3 rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-12"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-5 h-5" />
                Submit Report
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
} 